import type { VaultFileInDb, VaultFileRevisionInDb } from '@/api';
import { useVaultStore } from '@/stores/vaultStore';
import type { DroppedFile } from '@/utils/vaultFileDrop';
import { isArchive } from '@/utils/fileTypeUtils';

/** 3 MB, the chunk size the revision upload endpoints are tuned for. */
const CHUNK_SIZE = 1024 * 1024 * 3;
const MAX_CONCURRENT_UPLOADS = 5;

/**
 * Where one file goes: a folder (empty = the vault root) or into a contract.
 * An archive dropped into a folder is
 * unpacked next to itself unless `unpackArchives` is false — right for flows
 * that hand the file on as-is (a playbook run, a chat attachment).
 */
export type VaultUploadTarget =
  { directoryUid?: string; unpackArchives?: boolean } | { contractUid: string };

/** The vault file a single upload landed in, and the revision it created. */
export interface UploadedVaultRevision {
  fileUid: string;
  /** The created file, as created — without the revision it then gained. */
  vaultFile: VaultFileInDb;
  revision: VaultFileRevisionInDb;
}

export interface VaultUploadResult {
  uploaded: number;
  /** One line per file or folder that failed. */
  failed: string[];
}

export async function runWithConcurrency<T>(
  items: T[],
  fn: (item: T) => Promise<void>,
  limit = MAX_CONCURRENT_UPLOADS
): Promise<void> {
  const batchSize = Math.max(1, limit);
  for (let i = 0; i < items.length; i += batchSize) {
    await Promise.all(items.slice(i, i + batchSize).map(item => fn(item)));
  }
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

/**
 * The vault's upload engine: chunked revision uploads, and whole folder trees
 * mirrored into a target folder. Shared by the upload modal and the vault's
 * drop-to-upload so both go through one implementation.
 */
export function useVaultUpload(): {
  uploadFile: (
    file: File,
    target: VaultUploadTarget,
    onProgress?: (percent: number) => void
  ) => Promise<UploadedVaultRevision>;
  uploadTree: (
    files: DroppedFile[],
    parentDirectoryUid: string | undefined,
    onFileDone?: () => void
  ) => Promise<VaultUploadResult>;
  uploadToContract: (files: DroppedFile[], contractUid: string) => Promise<VaultUploadResult>;
} {
  const vaultStore = useVaultStore();

  async function uploadFile(
    file: File,
    target: VaultUploadTarget,
    onProgress?: (percent: number) => void
  ): Promise<UploadedVaultRevision> {
    const created = await vaultStore.createEmptyFile(
      'contractUid' in target ? { contractUid: target.contractUid } : target.directoryUid
    );
    const fileUid = created.uid;

    const uploadId = await vaultStore.initializeRevisionUpload(fileUid, {
      file_name: file.name,
      total_bytes: file.size,
    });
    onProgress?.(0);

    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      await vaultStore.uploadFileChunk(uploadId, chunk, i, totalChunks);
      onProgress?.(Math.round(((i + 1) / totalChunks) * 100));
    }

    const revision = await vaultStore.finalizeRevisionUpload(fileUid, uploadId);

    // An archive is only a carrier: unpacking it (into a `<name>_unzipped`
    // sibling folder) is what sends each file inside into the contract
    // pipeline — the backend never unpacks on upload by itself.
    if (
      'directoryUid' in target &&
      target.unpackArchives !== false &&
      isArchive(file.type, file.name)
    ) {
      try {
        await vaultStore.unpackFile(fileUid);
      } catch (error) {
        throw new Error(`Uploaded, but couldn’t unpack it: ${errorText(error)}`);
      }
    }

    return { fileUid, vaultFile: created, revision };
  }

  /**
   * Recreates the files' folder paths below `parentDirectoryUid` (parents
   * before children), then uploads every file into its folder. A folder that
   * fails to create sends its files to the nearest one that did not.
   */
  async function uploadTree(
    files: DroppedFile[],
    parentDirectoryUid: string | undefined,
    onFileDone?: () => void
  ): Promise<VaultUploadResult> {
    const failed: string[] = [];
    const directoryUids = new Map<string, string | undefined>([['', parentDirectoryUid]]);

    const paths = new Set<string>();
    for (const { dir } of files) {
      let path = '';
      for (const segment of dir.split('/').filter(Boolean)) {
        path = path ? `${path}/${segment}` : segment;
        paths.add(path);
      }
    }
    const byDepth = [...paths].sort((a, b) => a.split('/').length - b.split('/').length);

    for (const path of byDepth) {
      const lastSlash = path.lastIndexOf('/');
      const parentPath = lastSlash === -1 ? '' : path.slice(0, lastSlash);
      const parentUid = directoryUids.get(parentPath);
      try {
        const created = await vaultStore.createDirectory({
          name: path.slice(lastSlash + 1),
          parent_directory_uid: parentUid,
        });
        directoryUids.set(path, created.uid);
      } catch (error) {
        failed.push(`Couldn’t create the folder ${path}: ${errorText(error)}`);
        directoryUids.set(path, parentUid);
      }
    }

    let uploaded = 0;
    await runWithConcurrency(files, async ({ file, dir }) => {
      try {
        await uploadFile(file, { directoryUid: directoryUids.get(dir) ?? parentDirectoryUid });
        uploaded++;
      } catch (error) {
        failed.push(`Couldn’t upload ${file.name}: ${errorText(error)}`);
      }
      onFileDone?.();
    });

    return { uploaded, failed };
  }

  /** Every file becomes a contract file; folder structure is ignored. */
  async function uploadToContract(
    files: DroppedFile[],
    contractUid: string
  ): Promise<VaultUploadResult> {
    const failed: string[] = [];
    let uploaded = 0;
    await runWithConcurrency(files, async ({ file }) => {
      try {
        await uploadFile(file, { contractUid });
        uploaded++;
      } catch (error) {
        failed.push(`Couldn’t upload ${file.name}: ${errorText(error)}`);
      }
    });
    return { uploaded, failed };
  }

  return { uploadFile, uploadTree, uploadToContract };
}
